import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams } from 'react-router-dom';
import { Row, Col, Button } from 'reactstrap';
import { getModuleBasicInfo } from '../../../store/ModuleAction';
import * as Helper from '../../../util/Helper';
import { Link } from 'react-router-dom';
import StatusCode from '../../../util/StatusCode';
import * as InstanceLogsUtil from '../../../util/InstanceLogsUtil';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Search from '../../misc/Search';
import FilterBottomComponent from '../../misc/FilterBottomComponent';
import TableHorizontal, { InfoObject } from '../../misc/TableHorizontal';
import SelectInput from '../../misc/SelectInput';
import * as LogTypes from '../../../util/FlowSyncLoggerTypes';
import ModuleInstanceFilesModal from "../../storageContainer/ModuleInstanceFilesModal";
import RequestParser from '../../../util/RequestParser';
import { CoreModule, CoreModuleInstanceLogEntry, PageResult } from '../../../models/CoreModels';
import Copy from '../../misc/Copy';
import { useTranslation } from 'react-i18next'
import { getInstanceLogs, downloadInstanceLogs, getEventIds } from '../../../store/ModuleInstanceAction';
import { CacheRequestState, RequestState } from '../../../models/RequestState';
import { RootState } from '../../../store/store';


const Logs: React.FC = () => {
    const { t } = useTranslation(['flowSyncLoggerMessages', 'configuration', 'common']);
    const { moduleId, moduleInstanceId } = useParams();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);

    const instanceLogs = useSelector<RootState, CacheRequestState<PageResult<CoreModuleInstanceLogEntry>>>(state => state.moduleInstance.instanceLogs);
    const eventIds = useSelector<RootState, RequestState<number[]>>(state => state.moduleInstance.eventIds);
    const module = useSelector<RootState, RequestState<CoreModule>>(state => state.module.moduleBasicInfo);

    const filter = RequestParser.parseInstancelogSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        setSearchParams({ 'pageSize': '100'});

        if (moduleId) {
            if (moduleInstanceId) {
                getInstanceLogs(moduleId, moduleInstanceId, { ...filter })(dispatch);
                getEventIds(moduleId, moduleInstanceId)(dispatch);
            }
            getModuleBasicInfo(moduleId)(dispatch);
        }
    }, [dispatch, moduleId, moduleInstanceId]);

    useEffect(() => {
        if (moduleId && moduleInstanceId) {
            getInstanceLogs(moduleId, moduleInstanceId, filter, true)(dispatch);
        }
        window.scrollTo(0, 0);
    }, [dispatch, searchParams, moduleId, moduleInstanceId]);

    const updateFilter = useCallback((name: string, value?: string) => {
        searchParams.set('page', '1');
        if (value) {
            searchParams.set(name, value);
        } else {
            searchParams.delete(name);
        }
        setSearchParams(searchParams)
    }, [searchParams, setSearchParams])

    const debouncedSearch = debounce((search?: string) => updateFilter('search', search), 300);

    if (!moduleId || !moduleInstanceId) {
        return null;
    }

    const onChangePage = (page: number) => {
        searchParams.set('page', page.toString())
        setSearchParams(searchParams);
    }

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const downloadFile = () => {
        downloadInstanceLogs(moduleId, moduleInstanceId, { ...filter, pageSize: undefined, page: undefined });
    }

    const getObjectType = (log: CoreModuleInstanceLogEntry): JSX.Element | string | undefined => {
        if (log.objectSource === LogTypes.Flowsync.SourceName) {
            if (log.objectType === LogTypes.Flowsync.User) {
                return <Link to={`/user/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.Duty) {
                return <Link to={`/duty/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.Group) {
                return <Link to={`/group/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.Activity) {
                return <Link to={`/activity/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.Module) {
                return <Link to={`/module/${log.objectId}/status`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.OneRosterClass) {
                return <Link to={`/oneroster/class/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.ScheduleUser) {
                return <Link to={`/schedulesync/user/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.ScheduleEvent) {
                return <Link to={`/schedulesync/event/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
            if (log.objectType === LogTypes.Flowsync.GoogleDevice) {
                return <Link to={`/device/${log.objectId}`} target="_blank">{log.objectType}</Link>;
            }
        }
        return log.objectType;
    }

    const getObjectId = (log: CoreModuleInstanceLogEntry): JSX.Element | string | undefined => {
        if (!log.objectId) {
            return undefined;
        }

        let text = log.objectId;
        if (text.length > 20) {
            text = `${text.substring(0, 17)}...`
        }

        return (<span title={log.objectId} style={{ whiteSpace: 'pre' }}>{text}<Copy value={log.objectId} /></span>)
    }

    const renderInstanceLogs = () => {
        let instanceLogsList: InfoObject[] = [];

        if (instanceLogs.code === StatusCode.COMPLETE || (instanceLogs.code === StatusCode.PENDING && instanceLogs.data !== undefined)) {
            if (instanceLogs.data === undefined) {
                return [];
            }

            instanceLogsList = instanceLogs.data.values?.map((l, i) => {
                return ({
                    id: i.toString(),
                    data: [
                        { value: t(`severityLevels.${l.severity}`, { ns: 'configuration' }) },
                        { value: l.eventId },
                        { value: t(l.eventId.toString(), { ...l.arguments, defaultValue: l.message, ns: 'flowSyncLoggerMessages' }) },
                        { value: getObjectId(l) },
                        { value: getObjectType(l) },
                        { value: l.objectSource },
                        { value: <span style={{ whiteSpace: 'pre' }}>{Helper.formatDate(l.timestamp)}</span> },
                    ]
                })
            }) ?? [];
        }

        return (
            <TableHorizontal
                header={[{ value: t('log.tableHeaders.severity', { ns: 'configuration' }) }, { value: t('log.tableHeaders.eventId', { ns: 'configuration' }), className: "nowrap" }, { value: t('log.tableHeaders.message', { ns: 'configuration' }) }, { value: t('log.tableHeaders.objectId', { ns: 'configuration' }) }, { value: t('log.tableHeaders.objectType', { ns: 'configuration' }) }, { value: t('log.tableHeaders.objectSource', { ns: 'configuration' }) }, { value: t('log.tableHeaders.timestamp', { ns: 'configuration' }) }]}
                info={instanceLogsList}
                error={instanceLogs.code === StatusCode.ERROR}
                searching={instanceLogs.code === StatusCode.PENDING}
                noResMsg={t('log.notFound', { ns: 'configuration' })}
            />
        )
    }

    const renderFilter = () => (<>
        <Row className="s-switch">
            <Col sm="6">
                <Search className="margin-bottom1" value={search} onChange={onSearch} placeholder={t('log.searchLogs', { ns: 'configuration' })} />
            </Col>
            <Col sm="6" className="flex-row-right mb-2">
                <Button title={t('log.downLoadCsv', { ns: 'configuration' })} onClick={() => downloadFile()} disabled={(instanceLogs.code !== StatusCode.COMPLETE || !instanceLogs.data?.totalCount)}>{t('log.getLogs', { ns: 'configuration' })} <FontAwesomeIcon icon="file-download" className="icon ms-2" /></Button>

                {module.code === StatusCode.COMPLETE && module.data?.fileDownloadEnabled &&
                    <Button title={t('log.downLoadCsv', { ns: 'configuration' })} className="ms-2" onClick={() => setOpen(true)}>{t('log.getFiles', { ns: 'configuration' })}
                        <FontAwesomeIcon icon="folder" color="orange" className="icon ms-2" />
                    </Button>}
            </Col>
        </Row>
        <Row>
            <Col sm="6">
                <SelectInput
                    items={InstanceLogsUtil.severityArray.map(severity => ({ value: severity.id, text: `${t('filter.on', { ns: 'common' })} ${t(`severityLevels.${severity.id}`, { ns: 'configuration' })}` }))}
                    onChange={(name, value) => updateFilter(name, value?.toString())}
                    name='severity'
                    selected={filter?.severity}
                    defaultText={t('log.severityDefaultFilter', { ns: 'configuration' })}
                    disableSort={true}
                />
            </Col>
            <Col sm="6">
                <SelectInput
                    items={eventIds.code === StatusCode.COMPLETE ? eventIds.data.map(e => ({value: e, text: `${t('filter.on', { ns: 'common' })} ${e}` })) : []}
                    onChange={(name, value) => updateFilter(name, value?.toString())}
                    name='eventId'
                    selected={filter.eventId}
                    defaultText={t('log.eventIdDefaultFilter', { ns: 'configuration' })}
                />
            </Col>
        </Row>
    </>);

    return (
        <div className="mt-2">
            <div className="large-filter pl-40 pr-40">
                {renderFilter()}
            </div>
            <div className="pr-40 pl-40 pb-40 mt-2">
                {renderInstanceLogs()}
                {open && <ModuleInstanceFilesModal
                    moduleInstanceId={moduleInstanceId}
                    isOpen={open}
                    toggleModal={() => setOpen(!open)}
                />}
            </div>

            <FilterBottomComponent
                requestState={instanceLogs}
                clearFilter={clearFilter}
                updateFilter={onChangePage}
            />

        </div>
    )
}

export default Logs;
